/* global Excel, Office, window */

import { canUseScopedApis, filterToSpillAnchors, scanForSheetyBanksFormulas } from "./formulaScan";

// One frozen formula. Sheet name and sheet-relative addresses are stored
// separately so a renamed sheet degrades to a skipped entry instead of a
// mis-parsed address.
export interface SnapshotEntry {
  sheet: string; // worksheet name
  anchor: string; // anchor cell address, e.g. "B2"
  formula: string; // original formula text
  spill: string | null; // spill footprint address, e.g. "B2:F9", if the anchor spilled
  commentId: string | null; // provenance comment id, for cleanup on unfreeze
}

export interface SnapshotState {
  version: 1;
  createdAt: string; // ISO timestamp of the most recent freeze
  entries: SnapshotEntry[];
}

export type FreezeResult =
  | { supported: true; count: number; commentsFailed: number }
  | { supported: false };

export interface UnfreezeResult {
  restored: number;
  // Entries whose sheet was renamed, deleted, or protected since the freeze.
  // They stay in the registry so a later unfreeze can still pick them up.
  skipped: number;
}

// The registry persists inside the .xlsx via document settings, so a frozen
// workbook can be saved, shared, reopened, and still unfrozen later.
const SETTINGS_KEY = "sheetybanks.snapshot";

// Workbook-level defined name templates can key off to show frozen state
// (e.g. =IFERROR(SheetyBanks_Frozen,FALSE) — the IFERROR makes the same
// formula degrade to FALSE in workbooks that have never been frozen).
const FROZEN_NAME = "SheetyBanks_Frozen";

// The flag is cosmetic next to the settings registry — templates use it for
// labels/formatting — so failures never fail a freeze or unfreeze.
async function setFrozenFlag(on: boolean): Promise<void> {
  try {
    await Excel.run(async (context) => {
      const existing = context.workbook.names.getItemOrNullObject(FROZEN_NAME);
      // isNullObject is populated by the sync itself; no load needed.
      await context.sync();
      if (on && existing.isNullObject) {
        context.workbook.names.add(FROZEN_NAME, "=TRUE");
      } else if (!on && !existing.isNullObject) {
        existing.delete();
      }
      await context.sync();
    });
  } catch {
    // Cosmetic; ignore.
  }
}

export function getSnapshotState(): SnapshotState | null {
  const raw = Office.context.document.settings.get(SETTINGS_KEY) as SnapshotState | null;
  return raw && raw.entries && raw.entries.length > 0 ? raw : null;
}

function saveSnapshotState(state: SnapshotState | null): Promise<void> {
  const settings = Office.context.document.settings;
  if (state) {
    settings.set(SETTINGS_KEY, state);
  } else {
    settings.remove(SETTINGS_KEY);
  }
  return new Promise((resolve, reject) => {
    settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(new Error(result.error ? result.error.message : "Failed to save snapshot metadata"));
      }
    });
  });
}

// Range.address is fully qualified ("'My Sheet'!B2:F9"); the part after the
// last "!" is the sheet-relative address (sheet names may themselves contain
// "!", but the quoted prefix always ends at the final one).
function relativeAddress(qualified: string): string {
  return qualified.substring(qualified.lastIndexOf("!") + 1);
}

function provenanceText(entry: SnapshotEntry, createdAt: string): string {
  const when = new Date(createdAt).toLocaleString();
  const footprint = entry.spill ? ` (spilled over ${entry.spill})` : "";
  return `SheetyBanks snapshot — frozen ${when}${footprint} — formula: ${entry.formula}`;
}

/**
 * Replaces every SHEETYBANKS formula in the workbook with its current static
 * values. For spilled formulas the whole spill footprint is written at once,
 * so no partial overwrite can leave a #SPILL! behind. The original formula
 * and footprint are stored in the workbook's document settings (and echoed in
 * a provenance comment on each anchor) so unfreeze can restore the live model.
 */
export async function freezeSheetyBanksFormulas(): Promise<FreezeResult> {
  if (!canUseScopedApis()) {
    return { supported: false };
  }

  const newEntries = await Excel.run(async (context) => {
    const candidates = await scanForSheetyBanksFormulas(context);
    if (candidates.length === 0) return [];

    // Only anchors are frozen — a spilled anchor's footprint write covers
    // its members' cells. The filter leaves each anchor's address loaded.
    const anchors = await filterToSpillAnchors(context, candidates);

    const probes = anchors.map(({ ws, cell, formula }) => {
      const spillRange = cell.getSpillingToRangeOrNullObject();
      cell.load("values");
      spillRange.load("address, values");
      return { ws, cell, formula, spillRange };
    });
    await context.sync();

    const entries: SnapshotEntry[] = [];
    for (const probe of probes) {
      const spilled = !probe.spillRange.isNullObject;
      const target = spilled ? probe.spillRange : probe.cell;
      // Reading .values gives the loaded result grid; assigning it back
      // queues a static-value write over the exact same footprint.
      const frozenValues = target.values;
      target.values = frozenValues;

      entries.push({
        sheet: probe.ws.name,
        anchor: relativeAddress(probe.cell.address),
        formula: probe.formula,
        spill: spilled ? relativeAddress(probe.spillRange.address) : null,
        commentId: null,
      });
    }
    await context.sync();
    return entries;
  });

  if (newEntries.length === 0) {
    return { supported: true, count: 0, commentsFailed: 0 };
  }

  const previous = getSnapshotState();
  const state: SnapshotState = {
    version: 1,
    createdAt: new Date().toISOString(),
    entries: previous ? previous.entries.concat(newEntries) : newEntries,
  };
  // Persist before annotating: from here on the freeze is reversible even if
  // the comment pass below fails.
  await saveSnapshotState(state);

  await setFrozenFlag(true);

  const commentsFailed = await addProvenanceComments(newEntries, state.createdAt);
  // Comment ids were written into the entries; re-save so unfreeze can
  // clean the comments up by id.
  await saveSnapshotState(state);

  return { supported: true, count: newEntries.length, commentsFailed };
}

// Adds a provenance comment to each frozen anchor and records its id on the
// entry. Comments are cosmetic next to the settings registry, so failures
// (e.g. the user already has a comment on that cell) never fail the freeze —
// the batch is retried per entry and stragglers are just counted.
async function addProvenanceComments(entries: SnapshotEntry[], createdAt: string): Promise<number> {
  try {
    await Excel.run(async (context) => {
      const added = entries.map((entry) => {
        const ws = context.workbook.worksheets.getItem(entry.sheet);
        const comment = context.workbook.comments.add(
          ws.getRange(entry.anchor),
          provenanceText(entry, createdAt)
        );
        comment.load("id");
        return comment;
      });
      await context.sync();
      entries.forEach((entry, i) => {
        entry.commentId = added[i].id;
      });
    });
    return 0;
  } catch {
    // One bad cell fails the whole batch; retry one at a time so the rest
    // still get their comments.
    let failed = 0;
    for (const entry of entries) {
      try {
        await Excel.run(async (context) => {
          const ws = context.workbook.worksheets.getItem(entry.sheet);
          const comment = context.workbook.comments.add(
            ws.getRange(entry.anchor),
            provenanceText(entry, createdAt)
          );
          comment.load("id");
          // eslint-disable-next-line office-addins/no-context-sync-in-loop -- one Excel.run per entry is the point of this fallback
          await context.sync();
          entry.commentId = comment.id;
        });
      } catch {
        failed++;
      }
    }
    return failed;
  }
}

/**
 * Restores the formulas recorded by freezeSheetyBanksFormulas. Spill
 * footprints are cleared before the anchor formula is re-entered (leftover
 * static values would block the spill with #SPILL!). Entries whose sheet has
 * gone missing or protected are kept in the registry and reported as skipped.
 */
export async function unfreezeSheetyBanksFormulas(): Promise<UnfreezeResult> {
  const state = getSnapshotState();
  if (!state) {
    return { restored: 0, skipped: 0 };
  }

  // Restored formulas should re-fetch, not serve stale cached INFO results.
  window.sheetybanksClearCaches?.();

  const skippedEntries = await Excel.run(async (context) => {
    const sheetNames: string[] = [];
    for (const entry of state.entries) {
      if (sheetNames.indexOf(entry.sheet) === -1) sheetNames.push(entry.sheet);
    }
    const sheetByName: { [name: string]: Excel.Worksheet } = {};
    for (const name of sheetNames) {
      const ws = context.workbook.worksheets.getItemOrNullObject(name);
      ws.protection.load("protected");
      sheetByName[name] = ws;
    }
    const comments = context.workbook.comments;
    comments.load("items/id");
    await context.sync();

    const skipped: SnapshotEntry[] = [];
    const restoredCommentIds: string[] = [];
    for (const entry of state.entries) {
      const ws = sheetByName[entry.sheet];
      if (ws.isNullObject || ws.protection.protected) {
        skipped.push(entry);
        continue;
      }
      if (entry.spill) {
        ws.getRange(entry.spill).clear(Excel.ClearApplyTo.contents);
      }
      ws.getRange(entry.anchor).formulas = [[entry.formula]];
      if (entry.commentId) restoredCommentIds.push(entry.commentId);
    }

    for (const comment of comments.items) {
      if (restoredCommentIds.indexOf(comment.id) !== -1) comment.delete();
    }

    // Same reasoning as refresh: flush dependents of the restored cells in
    // manual calc mode without recomputing the rest of the workbook.
    if (skipped.length < state.entries.length) {
      context.workbook.application.calculate(Excel.CalculationType.recalculate);
    }
    await context.sync();
    return skipped;
  });

  await saveSnapshotState(skippedEntries.length > 0 ? { ...state, entries: skippedEntries } : null);

  // Skipped entries are still frozen, so the flag only clears once the
  // registry actually empties.
  if (skippedEntries.length === 0) {
    await setFrozenFlag(false);
  }

  return {
    restored: state.entries.length - skippedEntries.length,
    skipped: skippedEntries.length,
  };
}
